/**
 * Versioned Plugin Factory — `createVersionedPlugin` + `createSimplePlugin`
 *
 * Why eager-validation? The plugin definition is the load-bearing contract for
 * every read. A typo (skipped version, missing migration on a non-latest
 * entry, mismatched `latestSchema`) silently corrupts data on the first
 * migration. The factory runs an eager validation pass at module load — by the
 * time the app is ready, every plugin is structurally correct, or boot aborts.
 *
 * Ported near-verbatim from `ersatzteil-scraper` `src/lib/versioned/factory.ts`
 * (relative imports only — ADR-0005), trimmed to the three `OnMissingMigration`
 * modes this template uses. `createSimplePlugin` is **extracted here** from the
 * scraper's `system-config-keys.ts` (it never lived in `versioned/`) so it
 * routes through `createVersionedPlugin` and exercises `FrozenMap` — per plan
 * Phase 7 / spec §3.8.
 *
 * Runtime immutability (Q3): the two Map accessors are `FrozenMap` instances
 * because `Object.freeze(map)` does not freeze the `[[MapData]]` slot; the
 * plugin object itself is shallow-`Object.freeze`d. Schemas are NOT frozen —
 * Zod's parse pipeline mutates schema-internal memoisation slots.
 *
 * @see src-js/lib/storage/README.md (versioned-envelope code pattern)
 */

import type { z } from 'zod';

import { FrozenMap } from '../utils/frozen-map';
import type {
  IJsonSchemaPlugin,
  IVersionDefinition,
  IVersionedPluginConfig,
  MigrationFn,
  OnMissingMigration,
  VersionDefinition,
} from './types';

/**
 * Build a Map-shaped `IJsonSchemaPlugin` from the user-friendly versions array.
 * Throws on any inconsistency at module-load time.
 *
 * @throws Error when the versions array is empty.
 * @throws Error when versions are not strictly consecutive starting at 1.
 * @throws Error when a non-latest entry lacks `migrationToNext` in `'throw'` mode.
 * @throws Error when the latest entry carries a `migrationToNext`.
 * @throws Error when `latestSchema` is not the same reference as the latest entry's `schema`.
 */
export function createVersionedPlugin<
  TName extends string,
  TLatest,
  const TVersions extends ReadonlyArray<IVersionDefinition> = ReadonlyArray<IVersionDefinition>,
>(
  config: IVersionedPluginConfig<TName, TLatest, TVersions>
): IJsonSchemaPlugin<TName, TLatest> {
  const onMissingMigration: OnMissingMigration = config.onMissingMigration ?? 'migrate';

  if (config.versions.length === 0) {
    throw new Error(`Versioned plugin '${config.name}': versions array must not be empty.`);
  }

  // Sort defensively — canonical order is always ascending.
  const sorted: ReadonlyArray<VersionDefinition> = [...config.versions].sort(
    (a, b) => a.version - b.version
  );

  // CHECK 1 — versions strictly consecutive starting at 1.
  for (let index = 0; index < sorted.length; index++) {
    const expected = index + 1;
    if (sorted[index].version !== expected) {
      const observed = sorted.map((v) => v.version).join(', ');
      throw new Error(
        `Versioned plugin '${config.name}': versions must be 1, 2, 3, ... but got [${observed}].`
      );
    }
  }

  const latestEntry = sorted[sorted.length - 1];
  const currentVersion = latestEntry.version;

  // CHECK 2 — every non-latest entry must have `migrationToNext` in 'throw' mode.
  if (onMissingMigration === 'throw') {
    for (const entry of sorted) {
      if (entry.version === currentVersion) continue;
      if (!entry.migrationToNext) {
        throw new Error(
          `Versioned plugin '${config.name}': version ${entry.version} requires ` +
            `migrationToNext (or relax onMissingMigration to 'migrate' / 'reset-to-default').`
        );
      }
    }
  }

  // CHECK 3 — latest must NOT have migrationToNext (there is no next version).
  if (latestEntry.migrationToNext) {
    throw new Error(
      `Versioned plugin '${config.name}': latest version ${latestEntry.version} ` +
        `must not have migrationToNext (there is no next version).`
    );
  }

  // CHECK 4 — latestSchema must equal LATEST.schema (reference identity).
  if (config.latestSchema !== latestEntry.schema) {
    throw new Error(
      `Versioned plugin '${config.name}': latestSchema must be the same Zod object ` +
        `as the latest version's schema (reference identity).`
    );
  }

  // Build internal raw Maps. Iteration order matches sorted order.
  const rawSchemas = new Map<number, z.ZodType<unknown, z.ZodTypeDef, unknown>>();
  const rawMigrations = new Map<number, MigrationFn>();
  for (const entry of sorted) {
    rawSchemas.set(entry.version, entry.schema);
    if (entry.migrationToNext) rawMigrations.set(entry.version, entry.migrationToNext);
  }

  // FrozenMap so the `[[MapData]]` slot is truly inaccessible; shallow freeze
  // on the plugin object so its properties cannot be reassigned. Schemas stay
  // unfrozen (Zod mutates internal memoisation slots on first parse).
  const schemas = new FrozenMap<number, z.ZodType<unknown, z.ZodTypeDef, unknown>>(rawSchemas);
  const migrations = new FrozenMap<number, MigrationFn>(rawMigrations);

  const plugin = Object.freeze({
    name: config.name,
    currentVersion,
    schemas,
    latestSchema: config.latestSchema,
    migrations,
    onMissingMigration,
    defaultValue: config.defaultValue,
  });
  return plugin as unknown as IJsonSchemaPlugin<TName, TLatest>;
}

/**
 * Convenience wrapper for a v1-only versioned plugin (single schema, no
 * migrations). Extracted from the scraper's `system-config-keys.ts` so it
 * routes through `createVersionedPlugin` (exercising `FrozenMap`) — plan
 * Phase 7. NOT suitable for a plugin that needs a v1→v2 migration (it has no
 * migration slot); use `createVersionedPlugin` directly for those.
 */
export function createSimplePlugin<T>(
  name: string,
  schema: z.ZodType<T, z.ZodTypeDef, unknown>
): IJsonSchemaPlugin<string, T> {
  return createVersionedPlugin({
    name,
    versions: [{ version: 1, schema }],
    latestSchema: schema,
    onMissingMigration: 'throw',
  });
}
